import {
  type InvestorParticipation,
  PARTICIPATION_ACTION_LABELS,
  PARTICIPATION_STATES,
  type ParticipationState,
  type StaffParticipation,
} from '@fruuit/shared'
import { Button, Callout, StatusBadge, Timeline, type TimelineItem } from '@fruuit/ui'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { dateTime } from '../engagements/format'
import { type TransitionAction, TransitionDialog } from './TransitionDialog'

const EVENT_LABELS: Record<string, string> = {
  request: 'Request sent',
  acknowledge: 'Acknowledged',
  request_company_consent: 'Asked the company for consent',
  confirm: 'Confirmed',
  decline: 'Declined',
  withdraw: 'Withdrawn',
  reassign: 'Reassigned',
}

function stateLabel(state: ParticipationState): string {
  return PARTICIPATION_STATES.labels[state]
}

type Props =
  | { variant: 'investor'; request: InvestorParticipation; casePath?: string }
  | {
      variant: 'staff'
      request: StaffParticipation
      casePath?: string
      managers?: { id: string; name: string }[]
    }

/**
 * A participation request with its full event history (GAP-UI-018): every status change and
 * manager response with actor and time. Investors may withdraw their own open request; staff
 * see only the actions the server says they may take now.
 */
export function ParticipationCard(props: Props) {
  const r = props.request
  const [dialog, setDialog] = useState<TransitionAction | null>(null)

  const history: TimelineItem[] = [...r.events].reverse().map((e) => {
    const actor = 'actor_name' in e ? e.actor_name : e.actor.name
    const reassign =
      'to_manager' in e && e.to_manager
        ? ` to ${e.to_manager.name}${e.from_manager ? ` (from ${e.from_manager.name})` : ''}`
        : ''
    return {
      id: e.id,
      date: dateTime(e.created_at),
      dateNote: actor,
      title: `${EVENT_LABELS[e.action] ?? e.action}${reassign}`,
      body: e.note ? <span className="whitespace-pre-line">{e.note}</span> : undefined,
      status: e.to_status,
    }
  })

  const company = r.engagement?.issuer_name
  const requester =
    props.variant === 'investor' ? props.request.requested_by_name : props.request.requested_by.name
  const manager =
    props.variant === 'investor'
      ? props.request.manager_name
      : (props.request.assigned_manager?.name ?? null)
  const actions: TransitionAction[] =
    props.variant === 'investor'
      ? props.request.can_withdraw
        ? ['withdraw']
        : []
      : props.request.available_actions

  return (
    <article className="rounded-lg border border-line bg-surface p-4 sm:p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
            {company ?? 'Engagement no longer available'}
            {props.variant === 'staff' ? ` · ${props.request.org.name}` : ''}
          </p>
          <h3 className="mt-1 text-[15px] font-semibold text-ink">{r.request_type}</h3>
          <p className="mt-0.5 text-xs text-sage">
            Requested {dateTime(r.created_at)} · {requester}
            {r.meeting ? ` · ${r.meeting.title} (${dateTime(r.meeting.starts_at)})` : ''}
          </p>
        </div>
        <StatusBadge status={r.status} label={stateLabel(r.status)} />
      </header>
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-soft">{r.note}</p>
      <div className="mt-3 rounded-md bg-wash px-3.5 py-2.5 text-sm">
        {r.response ? (
          <>
            <p className="text-xs font-semibold text-teal-800">
              Manager response{manager ? ` · ${manager}` : ''} ·{' '}
              <span className="font-normal text-sage">{dateTime(r.responded_at)}</span>
            </p>
            <p className="mt-1 whitespace-pre-line text-ink">{r.response}</p>
          </>
        ) : (
          <p className="text-sage">
            {r.status === 'withdrawn'
              ? 'Withdrawn before a manager responded.'
              : `Waiting for ${manager ?? 'the assigned manager'} to respond.`}
          </p>
        )}
      </div>
      {r.withdrawn_at ? (
        <p className="mt-2 text-xs text-sage">Withdrawn {dateTime(r.withdrawn_at)}</p>
      ) : null}
      {props.variant === 'investor' && !r.engagement ? (
        <Callout tone="warning" className="mt-3">
          This engagement is no longer available to your organisation. You can still withdraw an
          open request.
        </Callout>
      ) : null}

      <details className="group mt-3">
        <summary className="cursor-pointer text-[13px] font-medium text-teal hover:underline">
          History ({r.events.length})
        </summary>
        <Timeline items={history} label="Request history" className="mt-4" />
      </details>

      <footer className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line-soft pt-3">
        {props.casePath && r.engagement ? (
          <Link
            to={props.casePath}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-teal hover:underline"
          >
            Open engagement <ArrowRight aria-hidden="true" className="size-3.5" />
          </Link>
        ) : (
          <span />
        )}
        {actions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => (
              <Button
                key={action}
                size="sm"
                variant={
                  action === 'decline' || action === 'withdraw'
                    ? 'secondary'
                    : action === 'reassign'
                      ? 'ghost'
                      : 'primary'
                }
                onClick={() => setDialog(action)}
              >
                {PARTICIPATION_ACTION_LABELS[action]}
              </Button>
            ))}
          </div>
        ) : null}
      </footer>
      {dialog ? (
        <TransitionDialog
          open
          onClose={() => setDialog(null)}
          requestId={r.id}
          revision={r.revision}
          action={dialog}
          managers={props.variant === 'staff' ? (props.managers ?? []) : []}
          currentManagerId={
            props.variant === 'staff' ? (props.request.assigned_manager?.id ?? null) : null
          }
        />
      ) : null}
    </article>
  )
}
